import type {
  ContentDatabase,
  ContentDatabaseConnection,
} from "@-/dev.improve.haniverse.database.content";
import { useCallback, useMemo } from "react";
import { withDatabaseLoggerProxy } from "@-/dev.improve.haniverse.lib.database.logger";
import { databaseLoggerFromLocation } from "@-/dev.improve.haniverse.lib.logger";
import { useGetContentDatabase, useGetContentDatabaseConnection } from "#use-content-database";

export function useContentDatabaseContextValue(): {
  db: ContentDatabase;
  dbConnection: ContentDatabaseConnection;
} {
  const getContentDatabase = useGetContentDatabase();
  const getContentDatabaseConnection = useGetContentDatabaseConnection();

  // oxlint-disable-next-line han-no-context-callbacks/no-callbacks-in-context-value -- stable logger-proxy factory keyed on getContentDatabase
  const db = useCallback<ContentDatabase>(
    (location) => withDatabaseLoggerProxy(getContentDatabase, databaseLoggerFromLocation(location)),
    [getContentDatabase],
  );
  const dbConnection = useMemo(
    () =>
      withDatabaseLoggerProxy(async (): Promise<ContentDatabaseConnection> => {
        // Downloaded packs provide the local content connection. No production consumer
        // reads this context field online; keep its existing non-null contract and fail clearly if
        // a future caller attempts to do so.
        const connection = await getContentDatabaseConnection();
        if (connection === null) {
          throw new Error("content database connection is unavailable in online mode");
        }
        return connection;
      }, null),
    [getContentDatabaseConnection],
  );

  return {
    db,
    dbConnection,
  };
}
